import { WindowToken } from './../../shared/window.token';
import { DOCUMENT } from '@angular/common';
import { AuthService } from './../../shared/services/auth.service';
import { Observable, BehaviorSubject } from 'rxjs';
import { NgxHttpClient } from './../../shared/ngx-http-client';
import { Inject, Injectable } from '@angular/core';

export interface ZendeskWindow extends Window {
  zESettings: any;
  zE: any;
}

export interface ZendeskWidgetConfiguration {
  jwt: string;
  configurationUrl: string;
}

@Injectable({
  providedIn: 'root',
})
export class ZendeskWidgetService {
  constructor(
    private http: NgxHttpClient,
    private authService: AuthService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(WindowToken) private windowRef: ZendeskWindow
  ) {}

  private _scriptLoaded = new BehaviorSubject<boolean>(false);
  public readonly zendeskIsInitialized = this._scriptLoaded.asObservable();
  private authUser = this.authService.authUser;

  public getWidgetConfiguration(): Observable<ZendeskWidgetConfiguration> {
    return this.http.get<ZendeskWidgetConfiguration>(
      '/zendeskwidget/getWidgetConfiguration'
    );
  }

  public initZendesk(zendeskConfig: ZendeskWidgetConfiguration) {
    // loading the zendesk script on the page needs to happen after the settings have been set, especially for authentication
    this.initZendeskSettings(zendeskConfig.jwt);
    this.initZendeskScript(zendeskConfig.configurationUrl);
  }

  public openWidget() {
    this.windowRef.zE('webWidget', 'open');
    this.windowRef.zE('webWidget', 'show');
  }

  private initZendeskSettings(token: string) {
    // these settings all need to exist on the Window for the webwidget to work properly.  Before removing, or modifying these,
    // please consult the zendesk widget documentation: https://developer.zendesk.com/embeddables/docs/widget/
    this.windowRef.zESettings = {
      webWidget: {
        authenticate: {
          jwtFn: function (callback) {
            callback(token);
          },
        },
        helpCenter: {
          suppress: !this.authUser?.defaultOrgInfo?.permissions
            ?.viewKnowledgeCenter,
        },
        contactForm: {
          fields: [
            { id: 'name', prefill: { '*': this.authUser?.viewerProfile.name } },
          ],
        },
      },
    };
  }

  private initZendeskScript(scriptUrl: string) {
    // this really isn't the "angular way" of doing things, but currently we are unable to add 3rd party libraries,
    // and zendesk also does not offer a package for their library.
    // script loading like this, however, does allow us to control when the script is added to the page - allowing for clients to turn this functionality off.
    const zendeskScript = this.document.createElement('script');
    zendeskScript.id = 'ze-snippet';
    zendeskScript.type = 'text/javascript';
    zendeskScript.src = scriptUrl;
    zendeskScript.onload = () => {
      this.windowRef.zE('webWidget', 'hide'); // hide the default zendesk widget launcher since we will create our own
      this.windowRef.zE('webWidget:on', 'close', () => {
        this.windowRef.zE('webWidget', 'hide'); // hide the default launcher
      });
      this._scriptLoaded.next(true);
    };
    this.document.head.appendChild(zendeskScript);
  }
}
